/**
 * Sales Calendar — the sales team's daily "To Do" workbench.
 *
 * ONE unified To Do list per day, combining:
 *  • Offer chases (Monday Conditional Offer + Offer Accepted groups)
 *  • Credit approved deals (Raise Doc's / Doc's Out — due EVERY day until snoozed)
 *  • LoanDisk top-up prospects (≥ threshold % repaid) & fully-paid re-loan props
 *  • Diary tasks (call backs, OB video calls with customer invites, texts)
 * Snooze/reschedule with notes, Clerk handler assignment, filters by
 * assignee & type, Aircall click-to-dial and Slack discuss (channel picker).
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Loader2, Plus,
  RefreshCw, Flame, FileText, TrendingUp, RotateCcw, Download, Zap, SlidersHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { getDirectory, mirrorSetColumn } from "@/lib/api";
import { ItemDrawer } from "@/pages/applications/MondayMirror";
import { useAuth } from "@/contexts/AuthContext";
import { TodoRow, KIND_META } from "./SalesTodoRow";
import { TodoFilterBar, applyTodoFilters, newFilter, useStatusValues } from "./TodoFilters";
import { AddTaskDialog, ChaseOfferDialog, DialDialog, DiscussDialog, ScheduleDialog, OutcomeDialog } from "./SalesDialogs";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const KIND_FILTERS = [
  { value: "all", label: "All types" },
  { value: "chase", label: "Offer chases" },
  { value: "credit_approved", label: "Docs chases (credit approved)" },
  { value: "topup", label: "Top-up prospects" },
  { value: "reloan", label: "Re-loan props (fully paid)" },
  { value: "task", label: "Tasks" },
];

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function prettyDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function SalesCalendar() {
  const { user } = useAuth();
  const me = user?.email || "sales";
  const today = new Date();
  const todayIso = isoDate(today);
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(todayIso);

  const [todo, setTodo] = useState({ items: [], counts: {}, options: {}, option_groups: {}, status_options: [], prospect_sync: {}, settings: {} });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");

  // Airtable-style panel filters (persisted per browser)
  const [panelFilters, setPanelFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("scal-todo-filters")) || []; } catch { return []; }
  });
  const [conjunction, setConjunction] = useState(() => localStorage.getItem("scal-todo-conjunction") || "all");
  const [showFilters, setShowFilters] = useState(() => (JSON.parse(localStorage.getItem("scal-todo-filters") || "[]")).length > 0);
  useEffect(() => { localStorage.setItem("scal-todo-filters", JSON.stringify(panelFilters)); }, [panelFilters]);
  useEffect(() => { localStorage.setItem("scal-todo-conjunction", conjunction); }, [conjunction]);

  const [addOpen, setAddOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [chaseTarget, setChaseTarget] = useState(null);
  const [dialTarget, setDialTarget] = useState(null);
  const [discussTarget, setDiscussTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const first = new Date(year, month, 1); first.setDate(first.getDate() - 7);
      const last = new Date(year, month + 1, 0); last.setDate(last.getDate() + 7);
      const r = await api.get("/sales/todo", { params: { from_date: isoDate(first), to_date: isoDate(last) } });
      setTodo(r.data);
    } catch (e) {
      toast.error("To Do failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getDirectory().then(setUsers).catch(() => setUsers([])); }, []);

  const pullLoanDisk = async () => {
    try {
      setPulling(true);
      await api.post(`/sales/prospects/sync?triggered_by=${encodeURIComponent(me)}`);
      toast.info("LoanDisk pull started — scanning the whole loan book takes a few minutes…");
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const r = await api.get("/sales/prospects/status");
        const s = r.data.sync || {};
        if (s.state === "done") {
          const st = s.stats || {};
          toast.success(
            `LoanDisk pull done — ${st.new_topups || 0} new top-ups, ${st.new_reloans || 0} new re-loans`
            + (s.first_run ? ` (first run: ${st.baselined || 0} historic fully-paid baselined)` : ""));
          load();
          return;
        }
        if (s.state === "failed") { toast.error("LoanDisk pull failed: " + (s.error || "unknown")); return; }
      }
      toast.error("LoanDisk pull is taking unusually long — check back shortly");
    } catch (e) {
      toast.error("LoanDisk pull failed: " + (e?.response?.data?.detail || e.message));
    } finally { setPulling(false); }
  };

  const bulkAddFullyPaid = async () => {
    const n = todo.counts?.baseline_reloans || 0;
    if (!window.confirm(`Add all ${n} historically fully-paid customers as re-loan props?\n\nThey will land on TODAY's To Do and roll forward daily until each is handled (not interested / new application / no contact).`)) return;
    try {
      setBulkAdding(true);
      const r = await api.post(`/sales/prospects/bulk-add-fully-paid?added_by=${encodeURIComponent(me)}`);
      toast.success(`${r.data.activated} historic fully-paid customers added to the To Do`);
      load();
    } catch (e) {
      toast.error("Bulk add failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBulkAdding(false); }
  };

  const filtered = useMemo(() => todo.items.filter(i => {
    if (kindFilter !== "all" && i.kind !== kindFilter) return false;
    if (assigneeFilter === "unassigned" && i.assigned_to) return false;
    if (assigneeFilter !== "all" && assigneeFilter !== "unassigned" && i.assigned_to !== assigneeFilter) return false;
    return true;
  }), [todo.items, kindFilter, assigneeFilter]);

  // Bucket by due date — anything overdue & still open rolls onto TODAY
  const byDate = useMemo(() => {
    const m = {};
    for (const i of filtered) {
      let key = i.due;
      if (i.due < todayIso) {
        if (i.kind !== "task" || i.status === "open") key = todayIso;
      }
      (m[key] = m[key] || []).push(i);
    }
    return m;
  }, [filtered, todayIso]);

  const dueToday = (byDate[todayIso] || []).filter(i => i.kind !== "task" || i.status === "open").length;
  const kpi = useMemo(() => {
    const k = { chase: 0, credit_approved: 0, topup: 0, reloan: 0, red: 0, amber: 0, green: 0 };
    for (const i of filtered) {
      if (k[i.kind] !== undefined) k[i.kind] += 1;
      if (i.priority && k[i.priority] !== undefined) k[i.priority] += 1;
    }
    return k;
  }, [filtered]);

  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7;
    const daysIn = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const goPrev = () => month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1);
  const goNext = () => month === 11 ? (setMonth(0), setYear(year + 1)) : setMonth(month + 1);
  const openAddTask = (prefill = null) => { setAddPrefill(prefill); setAddOpen(true); };

  const wrapSet = (itemId, columnId, label) => async (value) => {
    try {
      await mirrorSetColumn(itemId, columnId, value);
      toast.success(`${label} updated (synced to Monday)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      throw e;
    }
  };

  const mondayDeals = useMemo(() => todo.items.filter(i => i.kind === "chase" || i.kind === "credit_approved"), [todo.items]);
  const syncInfo = todo.prospect_sync || {};
  const statusValues = useStatusValues(todo.items);
  const dayItemsAll = byDate[selectedDate] || [];
  const dayItems = useMemo(
    () => applyTodoFilters(dayItemsAll, panelFilters, conjunction),
    [dayItemsAll, panelFilters, conjunction]);
  const activeFilterCount = panelFilters.filter(f => f.op === "is empty" || f.op === "is not empty" || String(f.value).trim() !== "").length;

  return (
    <div className="space-y-3 p-4" data-testid="sales-calendar">
      {/* HubSpot coming soon */}
      <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-1.5" data-testid="hubspot-banner">
        <Zap className="w-3.5 h-3.5 shrink-0" />
        <span><b>HubSpot sync coming soon</b> — direct sales deals & call-backs will land in this planner once the HubSpot API key is added in Portal Settings → API Credentials.</span>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div className="flex items-center gap-2">
          <CalIcon className="w-5 h-5 text-emerald-700" />
          <h1 className="text-xl font-semibold">Sales Calendar</h1>
          <span className="text-xs text-muted-foreground hidden sm:inline">One To Do — chases, docs, top-ups, re-loans & diary</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="scal-filter-assignee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all" className="text-xs">Everyone</SelectItem>
              <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
              {user?.email && <SelectItem value={user.email} className="text-xs">Mine ({user.email.split("@")[0]})</SelectItem>}
              {users.filter(u => u.email !== user?.email).map(u => (
                <SelectItem key={u.email} value={u.email} className="text-xs">{u.name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="scal-filter-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KIND_FILTERS.map(k => <SelectItem key={k.value} value={k.value} className="text-xs">{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8" onClick={load} data-testid="scal-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={() => openAddTask()} data-testid="scal-add-task">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add task
          </Button>
        </div>
      </div>

      {/* Month nav + KPI bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} data-testid="scal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <h2 className="text-lg font-medium min-w-[170px] text-center" data-testid="scal-month-label">{MONTHS[month]} {year}</h2>
          <Button variant="outline" size="sm" onClick={goNext} data-testid="scal-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setMonth(today.getMonth()); setYear(today.getFullYear()); setSelectedDate(todayIso); }} data-testid="scal-today">Today</Button>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap" data-testid="scal-kpis">
          <span className="flex items-center gap-1 font-semibold text-red-700" title="Actions due or overdue today (after filters)">
            <Flame className="w-3.5 h-3.5" />{dueToday} due today
          </span>
          <span className="flex items-center gap-2 px-3 border-x" title="Offer chase traffic lights (thresholds set in Portal Settings)">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-600 inline-block" /><span className="font-semibold text-red-700">{kpi.red}</span></span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /><span className="font-semibold text-amber-700">{kpi.amber}</span></span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /><span className="font-semibold text-emerald-700">{kpi.green}</span></span>
          </span>
          <span title="Offer chases"><Flame className="w-3 h-3 inline text-orange-600 -mt-0.5" /> <b>{kpi.chase}</b></span>
          <span title="Credit approved docs chases"><FileText className="w-3 h-3 inline text-pink-600 -mt-0.5" /> <b>{kpi.credit_approved}</b></span>
          <span title="LoanDisk top-up prospects"><TrendingUp className="w-3 h-3 inline text-emerald-600 -mt-0.5" /> <b>{kpi.topup}</b></span>
          <span title="Fully-paid re-loan props"><RotateCcw className="w-3 h-3 inline text-sky-600 -mt-0.5" /> <b>{kpi.reloan}</b></span>
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={pullLoanDisk} disabled={pulling} data-testid="scal-pull-loandisk"
            title={syncInfo.finished_at ? `Last LoanDisk pull: ${syncInfo.finished_at.slice(0, 16).replace("T", " ")} (${syncInfo.state})` : "Never pulled — first pull bulk-imports open loans past the threshold"}>
            {pulling ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
            {pulling ? "Pulling…" : "Pull LoanDisk"}
          </Button>
          {(todo.counts?.baseline_reloans || 0) > 0 && (
            <Button variant="outline" size="sm" className="h-7 text-[11px] border-sky-300 text-sky-700 hover:bg-sky-50"
              onClick={bulkAddFullyPaid} disabled={bulkAdding} data-testid="scal-bulk-add-fully-paid"
              title="Adds every historically fully-paid customer as a re-loan prop — they stay on the daily To Do until handled">
              {bulkAdding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
              Add {todo.counts.baseline_reloans} historic fully-paid
            </Button>
          )}
        </div>
      </div>

      {/* Body: grid + right-rail To Do */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr,460px] gap-4">
        <div className="border rounded-lg overflow-hidden bg-white">
          <div className="grid grid-cols-7 bg-slate-50 border-b text-[10px] font-semibold uppercase tracking-wider text-slate-600">
            {DOW.map(d => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7" data-testid="scal-grid">
            {grid.map((d, i) => {
              if (!d) return <div key={`e-${i}`} className="bg-slate-50/40 min-h-[108px] border-r border-b" />;
              const iso = isoDate(d);
              const list = byDate[iso] || [];
              const isToday = iso === todayIso;
              const isSelected = iso === selectedDate;
              const kindCounts = {};
              const tasks = [];
              for (const it of list) {
                if (it.kind === "task") tasks.push(it);
                else kindCounts[it.kind] = (kindCounts[it.kind] || 0) + 1;
              }
              return (
                <button type="button" key={iso} onClick={() => setSelectedDate(iso)}
                  className={`relative min-h-[108px] border-r border-b p-1.5 text-left hover:bg-emerald-50/40 transition ${isSelected ? "bg-emerald-100/40 ring-2 ring-inset ring-emerald-400" : ""} ${isToday ? "bg-amber-50/40" : ""}`}
                  data-testid={`scal-day-${iso}`}>
                  <div className={`text-xs font-semibold ${d < new Date(today.getFullYear(), today.getMonth(), today.getDate()) ? "text-slate-400" : "text-slate-700"}`}>
                    {d.getDate()}{isToday && <span className="ml-1 text-[9px] uppercase text-amber-700">Today</span>}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {Object.entries(kindCounts).map(([k, n]) => {
                      const km = KIND_META[k];
                      return (
                        <div key={k} className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${km.tone}`}
                          title={`${n} ${km.label.toLowerCase()}${n !== 1 ? "s" : ""} due`}>
                          <km.icon className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />{n} {km.label.toLowerCase()}{n !== 1 ? "s" : ""}
                        </div>
                      );
                    })}
                    {tasks.slice(0, 2).map(t => (
                      <div key={t.task_id}
                        className={`text-[10px] px-1.5 py-0.5 rounded border truncate bg-slate-50 text-slate-700 border-slate-200 ${t.status !== "open" ? "opacity-45 line-through" : ""}`}
                        title={t.title}>
                        <span className="font-semibold">{t.time ? `${t.time} ` : ""}{t.title}</span>
                      </div>
                    ))}
                    {tasks.length > 2 && <div className="text-[10px] text-slate-500 font-medium">+{tasks.length - 2} more tasks</div>}
                  </div>
                </button>
              );
            })}
          </div>
          {loading && (
            <div className="text-center text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" /> Syncing with Monday & LoanDisk…
            </div>
          )}
        </div>

        {/* Right rail: single To Do list for the selected day */}
        <div className="border rounded-lg bg-white flex flex-col max-h-[80vh]" data-testid="scal-todo-panel">
          <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold">To Do — {selectedDate === todayIso ? "Today" : prettyDate(selectedDate)}</div>
              <div className="text-[11px] text-muted-foreground">
                {activeFilterCount > 0
                  ? `${dayItems.length} of ${dayItemsAll.length} items (filtered)`
                  : `${dayItems.length} item${dayItems.length !== 1 ? "s" : ""}`}
                {selectedDate === todayIso && " (incl. overdue rolled forward)"}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant={activeFilterCount > 0 ? "default" : "ghost"} size="sm"
                className={`h-7 text-[11px] ${activeFilterCount > 0 ? "bg-emerald-700 hover:bg-emerald-800" : ""}`}
                onClick={() => { setShowFilters(v => !v); if (!showFilters && panelFilters.length === 0) setPanelFilters([newFilter()]); }}
                data-testid="scal-panel-filters-toggle">
                <SlidersHorizontal className="w-3 h-3 mr-1" /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => openAddTask({ date: selectedDate })}>
                <Plus className="w-3 h-3 mr-0.5" /> Task
              </Button>
            </div>
          </div>
          {showFilters && (
            <TodoFilterBar filters={panelFilters} setFilters={setPanelFilters}
              conjunction={conjunction} setConjunction={setConjunction}
              users={users} statusValues={statusValues} />
          )}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
            ) : dayItems.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Nothing due on this day. 🎉</div>
            ) : (
              <ul className="divide-y" data-testid="scal-todo-list">
                {dayItems.map(it => (
                  <TodoRow key={it.key} item={it} me={me} users={users}
                    statusOptions={todo.status_options || []}
                    onChanged={load}
                    onOpenCard={setDrawerId}
                    onChase={setChaseTarget}
                    onDial={setDialTarget}
                    onDiscuss={setDiscussTarget}
                    onAddTask={openAddTask}
                    onSchedule={setScheduleTarget}
                    onOutcome={setOutcomeTarget} />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <AddTaskDialog open={addOpen} onClose={() => setAddOpen(false)} me={me}
        prefill={addPrefill} defaultDate={selectedDate || todayIso}
        deals={mondayDeals} users={users} onCreated={() => { setAddOpen(false); load(); }} />

      <ChaseOfferDialog deal={chaseTarget} onClose={() => setChaseTarget(null)}
        rejectReasons={todo.options?.color_mm12vds8 || []}
        onDone={() => { setChaseTarget(null); load(); }} />

      <DialDialog deal={dialTarget} me={me} onClose={() => setDialTarget(null)} />

      <DiscussDialog deal={discussTarget} me={me} onClose={() => setDiscussTarget(null)} />

      <ScheduleDialog target={scheduleTarget} me={me} onClose={() => setScheduleTarget(null)}
        onDone={() => { setScheduleTarget(null); load(); }} />

      <OutcomeDialog target={outcomeTarget} me={me} onClose={() => setOutcomeTarget(null)}
        onDone={() => { setOutcomeTarget(null); load(); }} />

      {drawerId && (
        <ItemDrawer
          itemId={drawerId}
          options={todo.options || {}}
          optionGroups={todo.option_groups || {}}
          onClose={() => { setDrawerId(null); load(); }}
          wrapSet={wrapSet}
        />
      )}
    </div>
  );
}
